import { cn } from '@/lib/utils'

interface LoyaltyStatusProps {
  loyalty: number
  className?: string
}

interface LoyaltyLevel {
  min: number
  max: number
  label: string
  status: string
  description: string
  colorClass: string
  bgClass: string
}

const LOYALTY_LEVELS: LoyaltyLevel[] = [
  {
    min: 80,
    max: 100,
    label: 'Devoted',
    status: 'devoted',
    description: 'Unwavering commitment to the party cause',
    colorClass: 'text-green-600 dark:text-green-400',
    bgClass: 'bg-green-500',
  },
  {
    min: 60,
    max: 79,
    label: 'Loyal',
    status: 'loyal',
    description: 'Reliable party member, unlikely to defect',
    colorClass: 'text-emerald-600 dark:text-emerald-400',
    bgClass: 'bg-emerald-500',
  },
  {
    min: 40,
    max: 59,
    label: 'Neutral',
    status: 'neutral',
    description: 'Committed but could be swayed under pressure',
    colorClass: 'text-yellow-600 dark:text-yellow-400',
    bgClass: 'bg-yellow-500',
  },
  {
    min: 20,
    max: 39,
    label: 'Disgruntled',
    status: 'disgruntled',
    description: 'Unhappy with party direction, may cause problems',
    colorClass: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-500',
  },
  {
    min: 0,
    max: 19,
    label: 'At Risk of Defection',
    status: 'at-risk',
    description: 'Serious loyalty issues, high defection chance',
    colorClass: 'text-red-600 dark:text-red-400',
    bgClass: 'bg-red-500',
  },
]

function getLoyaltyLevel(loyalty: number): LoyaltyLevel {
  const clamped = Math.max(0, Math.min(100, loyalty))
  for (const level of LOYALTY_LEVELS) {
    if (clamped >= level.min && clamped <= level.max) {
      return level
    }
  }
  return LOYALTY_LEVELS[LOYALTY_LEVELS.length - 1]
}

export function LoyaltyStatus({ loyalty, className }: LoyaltyStatusProps) {
  const level = getLoyaltyLevel(loyalty)
  const percentage = Math.round(loyalty)

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Loyalty</span>
        <span className="text-sm font-semibold">{percentage}%</span>
      </div>

      <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full transition-all', level.bgClass)}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className={cn('text-sm font-semibold uppercase', level.colorClass)}>
          {level.label}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">{level.description}</p>
    </div>
  )
}
